import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Paper, Skeleton, Card, Alert } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Bar } from "react-chartjs-2";
import axios from "axios";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import { baseURL } from "../../utils/StaticVariables";
import DesBtn from "../Reusable/DesBtn";
import OrderFilter from "../Charts/OrderFilter";

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top", labels: { color: "gray" } },
    title: { display: false },
  },
  scales: {
    x: { ticks: { color: "gray" } },
    y: {
      ticks: { color: "gray" },
      min: 0,
      max: 1,
    },
  },
};

const ModelAuditChart = ({
  featureTitle = "Model Accuracy",
  apiEndpoint = "",
}) => {
  const [modelData, setModelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const [filter, setFilter] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { ...filter };
        if (params.locations) { params.location = params.locations; delete params.locations; }
        if (params.buildings) { params.building = params.buildings; delete params.buildings; }
        if (params.zones) { params.zone = params.zones; delete params.zones; }

        if (apiEndpoint) {
          const { data } = await axios.get(apiEndpoint, {
            headers: { Authorization: `Bearer ${token}` },
            params,
          });
          setModelData(data);
        } else {
          // Standard model accuracy endpoint
          const { data } = await axios.get(`${baseURL}shoplifting/dashboard/model-accuracy`, {
            headers: { Authorization: `Bearer ${token}` },
            params,
          });
          setModelData(data);
        }
      } catch (err) {
        console.error("Error fetching model accuracy audit data:", err);
        setError("Unable to load model confidence statistics.");
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchData();
  }, [filter, token, apiEndpoint]);

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: (
        <OrderFilter ref={childRef} filter={filter} changeFilterHandle={setFilter} />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  // per_camera is the shared evidence-analytics confidence-audit shape.
  const averagePerCamera =
    modelData?.average_per_camera ||
    modelData?.per_camera ||
    modelData?.data ||
    [];

  const cameraLabels = averagePerCamera.map((item) => item.camera_name || item.name || "Unknown");
  const confidenceData = averagePerCamera.map((item) =>
    typeof item.avg_confidence === "number"
      ? parseFloat(item.avg_confidence.toFixed(2))
      : typeof item.confidence === "number"
      ? parseFloat(item.confidence.toFixed(2))
      : 0.85
  );

  return (
    <Card sx={{ p: 2, height: "auto", bgcolor: "background.paper", mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Model Accuracy & Confidence Audit — {featureTitle}
        </Typography>
        <DesBtn text="Filter" handle={openFilter}>
          <FilterAltOutlinedIcon />
        </DesBtn>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Skeleton variant="rectangular" height="400px" sx={{ borderRadius: 2 }} />
      ) : (
        <Paper elevation={0} sx={{ p: 2, height: 450, bgcolor: "background.paper" }}>
          <Typography variant="subtitle1" mb={2}>
            Average Confidence Score per Camera Over Detected Incidents
          </Typography>
          <Box sx={{ height: 380 }}>
            <Bar
              data={{
                labels: cameraLabels.length ? cameraLabels : ["No Data Available"],
                datasets: [
                  {
                    label: "Avg Confidence Score",
                    data: confidenceData.length ? confidenceData : [0],
                    backgroundColor: "#36a2eb",
                    borderRadius: 4,
                  },
                ],
              }}
              options={commonOptions}
            />
          </Box>
        </Paper>
      )}
    </Card>
  );
};

export default ModelAuditChart;
